import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getDonationTotalForYear, postDonationSession } from "../services/AnimalSplitCore";
import GroupDonationInitiatorModal from "./GroupDonationInitiatorModal";
import "./SettleConfirmModal.css";
import "../containers/ModalTheme.css";

const GOAL_USD = 550;
const GOAL_CENTS = GOAL_USD * 100;

export default function SettleConfirmModal({
  modalId,
  groupId,
  groupName,
  contributors = [],
  confirmText,
  cancelText,
  confirmVariantClassName = "as-btn-primary-gradient",
  onConfirm,
  confirmDisabled = false,
  cancelDisabled = false,
}) {
  const { t } = useTranslation();
  const [totalCents, setTotalCents] = useState(null);
  const [isDonating, setIsDonating] = useState(false);
  const [isCreatingDonation, setIsCreatingDonation] = useState(false);

  const campaignYear = useMemo(() => new Date().getFullYear(), []);
  const groupDonationModalId = useMemo(() => `${modalId}-group-donation`, [modalId]);

  // Fetch donation progress when component mounts (data will be ready when modal opens)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getDonationTotalForYear(campaignYear);
        if (cancelled) return;
        setTotalCents(Number(res?.totalCents ?? 0));
      } catch (e) {
        console.error("Failed to fetch donation total:", e);
        if (!cancelled) setTotalCents(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campaignYear]);

  const handleDonateAndSettle = useCallback(async () => {
    if (isDonating) return;
    
    try {
      setIsDonating(true);
      
      const currentYear = new Date().getFullYear();
      const truncatedGroupName = groupName 
        ? (groupName.length > 10 ? `${groupName.substring(0, 10)}...` : groupName)
        : "Anonymous";
      const donationNote = `support animalsplit ${currentYear}`;
      
      const session = await postDonationSession({
        amount: 100, // $1
        alias: truncatedGroupName,
        note: donationNote,
        returnUrl: window.location.href,
        groupId: groupId,
        donationType: "settle", // Backend will auto-settle after payment succeeds
      });
      
      if (!session || !session.url) {
        throw new Error("Invalid session response");
      }
      
      // Mark that user has donated
      try {
        localStorage.setItem("hasDonated", "true");
        const today = new Date().toISOString().slice(0, 10);
        localStorage.setItem("lastReturnDonateShown", today);
      } catch (e) {
        // ignore localStorage failures
      }
      
      // Redirect to Stripe checkout (webhook will handle settle after payment)
      window.location.href = session.url;
    } catch (error) {
      console.error("Donation failed:", error);
      alert(t("donation.payment_failed") || "Unable to initiate payment. Please try again.");
      setIsDonating(false);
    }
  }, [isDonating, groupId, groupName, t]);

  const handleOpenGroupDonationModal = useCallback(() => {
    try {
      const currentEl = document.getElementById(modalId);
      const nextEl = document.getElementById(groupDonationModalId);
      if (!currentEl || !nextEl || !window?.bootstrap?.Modal) return;

      const currentInst =
        window.bootstrap.Modal.getInstance(currentEl) || new window.bootstrap.Modal(currentEl);

      const showNext = () => {
        currentEl.removeEventListener("hidden.bs.modal", showNext);
        const nextInst =
          window.bootstrap.Modal.getInstance(nextEl) || new window.bootstrap.Modal(nextEl);
        nextInst.show();
      };

      currentEl.addEventListener("hidden.bs.modal", showNext);
      currentInst.hide();
    } catch (e) {
      // ignore
    }
  }, [groupDonationModalId, modalId]);

  const clampPct = (pct) => Math.max(0, Math.min(100, pct));
  const hasTotal = Number.isFinite(Number(totalCents));
  const progressPct = clampPct(
    Math.round(((hasTotal ? Number(totalCents) : 0) / GOAL_CENTS) * 100),
  );

  const formatUsd = (cents) => {
    const v = (Number(cents ?? 0) || 0) / 100;
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: v % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(v);
  };

  const body = (
    <div>
      <p className="mb-2">
        {t("group.settle_confirm_body") ||
          "This will calculate settlements once and lock edits to contributors and transactions."}
      </p>
      <p className="mb-2 small text-muted fw-bold">
        <i className="bi bi-info-circle me-1" />
        {t("group.settle_confirm_undo_hint") ||
          "You can reset the settlement later in Settings, but all payment tracking data will be lost."}
      </p>

      <div className="settle-donate-card-compact">
        <div className="settle-donate-content">
          <div className="settle-donate-media">
            <img
              src={`${process.env.PUBLIC_URL || ""}/images/image/donation.png`}
              alt="Support AnimalSplit"
              className="settle-donate-image"
              loading="lazy"
            />
          </div>
          <div className="settle-donate-text">
            <strong className="settle-donate-title">
              {t("group.settle_donate_prompt") || "💝 Consider supporting AnimalSplit"}
            </strong>
            <p className="settle-donate-body">
              {t("group.settle_donate_body") || 
                "Your donation helps keep this app free and helps us continue to improve it."}
            </p>
            {hasTotal && (
              <div className="settle-donate-progress">
                <div className="settle-donate-progress-row">
                  <span className="settle-donate-progress-label">
                    {t("return_donate.progress_label", { year: campaignYear }) || `${campaignYear} Goal`}
                  </span>
                  <span className="settle-donate-progress-value">
                    {formatUsd(totalCents)} / {formatUsd(GOAL_CENTS)}
                  </span>
                </div>
                <div className="settle-donate-progress-track">
                  <div
                    className="settle-donate-progress-fill"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <div className="settle-donate-progress-meta">
                  {progressPct}% of goal reached
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const modal = (
    <div className="modal fade" id={modalId} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content as-theme as-modal">
          <div className="modal-header">
            <div className="as-modal-header-row">
              <div className="as-modal-title-row">
                <span className="as-modal-icon-badge" aria-hidden="true">🧾</span>
                <h5 className="modal-title">
                  {t("group.settle_confirm_title") || "Settle this group?"}
                </h5>
              </div>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={t("general.close") || "Close"}
                disabled={cancelDisabled}
              />
            </div>
          </div>
          <div className="modal-body">{body}</div>
          <div className="modal-footer as-modal-footer">
            <button
              type="button"
              className="btn btn-sm as-btn-soft"
              data-bs-dismiss="modal"
              disabled={cancelDisabled}
            >
              {cancelText}
            </button>
            <button
              type="button"
              className="btn btn-sm as-btn-soft"
              onClick={onConfirm}
              disabled={confirmDisabled || isDonating || isCreatingDonation}
            >
              {confirmText}
            </button>
            <button
              type="button"
              className="btn btn-sm as-btn-soft"
              onClick={handleOpenGroupDonationModal}
              disabled={confirmDisabled || isDonating || isCreatingDonation}
            >
              {isCreatingDonation
                ? (t("group.settling") || "Settling…")
                : (t("group.settle_create_donation_and_settle") || "🎁 Donate as group & settle")}
            </button>
            <button
              type="button"
              className="btn btn-sm as-btn-primary-gradient"
              onClick={handleDonateAndSettle}
              disabled={confirmDisabled || isDonating || isCreatingDonation}
            >
              {isDonating
                ? (t("donation.redirecting") || "Redirecting…")
                : (t("group.settle_donate_and_settle") || "💝 Donate $1 & Settle")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render into document.body so it's not affected by parent stacking contexts/transforms.
  const groupDonationModal = (
    <GroupDonationInitiatorModal
      modalId={groupDonationModalId}
      groupId={groupId}
      groupName={groupName}
      contributors={contributors}
      // Keep state in this parent so we can disable buttons consistently
      isCreatingDonation={isCreatingDonation}
      setIsCreatingDonation={setIsCreatingDonation}
    />
  );

  return typeof document !== "undefined"
    ? createPortal(
        <>
          {modal}
          {groupDonationModal}
        </>,
        document.body,
      )
    : (
        <>
          {modal}
          {groupDonationModal}
        </>
      );
}

